export type MaterialTransformation = 'open' | 'thaw';

export interface MaterialDerivedFoodLike {
  id: string;
  name: string;
  default_uom: string;
  creation_source?: string | null;
  recipe_id?: string | null;
  archived_at?: string | null;
}

export type MaterialDerivedFoodPlan<T extends MaterialDerivedFoodLike> =
  | { kind: 'existing'; food: T; foodId: string; name: string }
  | { kind: 'create'; foodId: string; name: string };

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizedUom(value: string): string {
  return value.trim().toLowerCase();
}

async function deterministicUuid(namespace: string): Promise<string> {
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(namespace)),
  );
  const bytes = digest.slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function materialDerivedFoodName(
  sourceName: string,
  transformation: MaterialTransformation,
): string {
  return `${sourceName.trim()} (${transformation === 'thaw' ? 'scongelato' : 'aperto'})`;
}

export async function materialDerivedFoodIdFor(
  tenantId: string,
  sourceFoodId: string,
  transformation: MaterialTransformation,
): Promise<string> {
  if (!UUID_PATTERN.test(tenantId) || !UUID_PATTERN.test(sourceFoodId)) {
    throw new Error('Identità tenant o materia prima non valida');
  }
  return deterministicUuid(
    `material_derived_food|${tenantId.toLowerCase()}|${sourceFoodId.toLowerCase()}|${transformation}`,
  );
}

export async function planMaterialDerivedFood<T extends MaterialDerivedFoodLike>(input: {
  tenantId: string;
  sourceFoodId: string;
  sourceName: string;
  sourceUom: string;
  transformation: MaterialTransformation;
  foods: readonly T[];
}): Promise<MaterialDerivedFoodPlan<T>> {
  const foodId = await materialDerivedFoodIdFor(
    input.tenantId,
    input.sourceFoodId,
    input.transformation,
  );
  const name = materialDerivedFoodName(input.sourceName, input.transformation);
  const existing = input.foods.find((food) => food.id === foodId);
  if (!existing) return { kind: 'create', foodId, name };

  if (
    existing.archived_at
    || existing.creation_source !== 'quick_label'
    || existing.recipe_id != null
    || existing.name !== name
    || normalizedUom(existing.default_uom) !== normalizedUom(input.sourceUom)
  ) {
    throw new Error(
      'La scheda automatica del prodotto trasformato è occupata o non coerente: correggi l’anagrafica prima di stampare',
    );
  }
  return { kind: 'existing', food: existing, foodId, name };
}

export function isAutoMaterialDerivedFood(
  food: Pick<MaterialDerivedFoodLike, 'name' | 'creation_source'>,
): boolean {
  return food.creation_source === 'quick_label'
    && /\s\((?:aperto|scongelato)\)$/i.test(food.name.trim());
}

export function materialTransformationFromDerivedFood(
  food: Pick<MaterialDerivedFoodLike, 'name' | 'creation_source'>,
): MaterialTransformation | null {
  if (food.creation_source !== 'quick_label') return null;
  const name = food.name.trim();
  if (/\s\(scongelato\)$/i.test(name)) return 'thaw';
  if (/\s\(aperto\)$/i.test(name)) return 'open';
  return null;
}
